import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api/client";
import { courseUnitsApi, type CourseUnit, type UnitOperation } from "@/lib/api/courseUnits";
import { clearGuidanceRecovery, GUIDANCE_RECOVERY_CLEARED, readPending, readRecovery, writePending, writeRecovery, type PendingGuidance } from "./guidance-recovery";
import { activitiesValid, draftActivities, savedActivities, type DraftActivity } from "./unit-planner";

/**
 * One private teaching-plan draft against one course unit (ADR 0030, ADR 0033).
 *
 * The Session plan card edits through this hook wherever it appears (the session
 * page and the course plan), so autosave, operation receipts, stale-revision
 * choices, unknown-operation reconciliation, recovery storage and sign-out
 * clearing have one implementation. Surfaces own their words and layout; the
 * hook owns state.
 */
export type UnitDraft = { topic: string; objectives: string; preparation: string; recap: string; followUp: string; sessionId: string; activities: DraftActivity[]; notes: string };
export type UnitDraftDefaults = { topic?: string; sessionId?: string };
export type UnitReceipt = { kind: UnitOperation["kind"]; unit: CourseUnit };
export type UnitPublishOutcome =
  | { kind: "confirmed" }
  | { kind: "refused"; reason: string }
  /** The send's own operation id, so its receipt can be read later; never answered from a later read of the plan. */
  | { kind: "uncertain"; reason: string; operationId: string };

/** How long an operation's receipt is said once it lands (object checklist item 5: an event, not a badge). */
export const RECEIPT_SAID_FOR_MS = 5000;

export const objectiveLines = (text: string) => text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
export const MAX_OBJECTIVES = 12;
export const validDraft = (draft: UnitDraft) => !!draft.topic.trim() && objectiveLines(draft.objectives).length <= MAX_OBJECTIVES && activitiesValid(draft.activities);
export const draftFromUnit = (unit: CourseUnit | null, defaults: UnitDraftDefaults = {}): UnitDraft => ({
  topic: unit?.topic ?? defaults.topic ?? "", objectives: unit?.learning_objectives.join("\n") ?? "",
  preparation: unit?.preparation_guidance ?? "", recap: unit?.recap ?? "", followUp: unit?.follow_up ?? "",
  sessionId: unit ? unit.session_id ?? "" : defaults.sessionId ?? "",
  activities: draftActivities(unit?.activities), notes: unit?.teaching_notes ?? "",
});

/** What a draft saves as: the server's normalized content, so trimming is never an unsaved edit. */
export const draftContent = (draft: UnitDraft) => ({
  topic: draft.topic.trim(), learning_objectives: objectiveLines(draft.objectives),
  preparation_guidance: draft.preparation.trim() || null, recap: draft.recap.trim() || null,
  follow_up: draft.followUp.trim() || null, session_id: draft.sessionId || null,
  activities: savedActivities(draft.activities), teaching_notes: draft.notes.trim() || null,
});

/** The existing plan a 409 `class_plan_exists` names, or null for any other refusal. */
export function classPlanExists(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const detail = error.detail;
  if (typeof detail !== "object" || detail === null) return null;
  const { code, unit_id: unitId } = detail as { code?: unknown; unit_id?: unknown };
  return code === "class_plan_exists" && typeof unitId === "string" ? unitId : null;
}

/** A new plan's edits laid onto the session's existing plan: text appended, activities after its own. */
function mergeOnto(existing: CourseUnit, baseline: UnitDraft, mine: UnitDraft): UnitDraft {
  const base = draftFromUnit(existing);
  const text = (key: "objectives" | "preparation" | "recap" | "followUp" | "notes") =>
    mine[key] === baseline[key] ? base[key] : base[key].trim() ? `${base[key]}\n${mine[key]}` : mine[key];
  return {
    ...base, objectives: text("objectives"), preparation: text("preparation"), recap: text("recap"),
    followUp: text("followUp"), notes: text("notes"), activities: [...base.activities, ...mine.activities],
  };
}

type Recovery = { draft: UnitDraft; revision: number | null; unitId: string };

export function useUnitDraft({ courseId, unit, position, recoveryKey, defaults = {}, onSaved, onConflict }: {
  courseId: string; unit: CourseUnit | null; position: number; recoveryKey: string; defaults?: UnitDraftDefaults;
  onSaved: (unit: CourseUnit) => void;
  onConflict: (id: string) => Promise<CourseUnit | null>;
}) {
  const [recovered] = useState(() => readRecovery<Recovery>(recoveryKey));
  const [unitId, setUnitId] = useState(() => unit?.id ?? readPending(recoveryKey)?.unitId ?? recovered?.unitId ?? crypto.randomUUID());
  const [acknowledged, setAcknowledged] = useState(unit);
  const [draft, setDraft] = useState(() => ({ ...draftFromUnit(unit, defaults), ...recovered?.draft }));
  const draftRef = useRef(draft); draftRef.current = draft;
  const persistenceEnabled = useRef(true);
  const [expected, setExpected] = useState(recovered?.revision ?? unit?.revision ?? null);
  const [pending, setPending] = useState(() => readPending(recoveryKey));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [receipt, setReceipt] = useState<UnitReceipt | null>(null);
  const [refused, setRefused] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const [conflict, setConflict] = useState<CourseUnit | null>(null);
  const [review, setReview] = useState(false);
  const [autosavePaused, setAutosavePaused] = useState(false);
  const [comparisonUnavailable, setComparisonUnavailable] = useState(false);
  /** The session's existing plan replaced this draft's create; the draft now edits it. */
  const [adoptedExisting, setAdoptedExisting] = useState(false);
  /** A placement refused because the chosen session already has a plan: that session and its plan. */
  const [classTaken, setClassTaken] = useState<{ sessionId: string; unitId: string } | null>(null);
  /** Set when an operation starts; the surface consumes it to restore focus. */
  const operationFocus = useRef(false);
  const baseline = draftFromUnit(acknowledged, defaults);
  const dirty = JSON.stringify(draftContent(draft)) !== JSON.stringify(draftContent(baseline));
  const exactDraftIsClean = JSON.stringify(draft) === JSON.stringify(baseline);
  // Assignment changes do not advance a plan revision. Refresh only the
  // authority fact, preserving the teacher's text and unresolved operation.
  useEffect(() => {
    if (!unit || unit.id !== unitId) return;
    setAcknowledged(saved => saved && saved.can_publish !== unit.can_publish
      ? { ...saved, can_publish: unit.can_publish } : saved);
  }, [unit, unitId]);
  // A reread can advance a clean editor, but never a keystroke, review or unresolved operation.
  useEffect(() => {
    if (!unit || !acknowledged || unit.id !== unitId || unit.revision <= acknowledged.revision
      || dirty || !exactDraftIsClean || busy || pending || refused || review || conflict
      || comparisonUnavailable || storageError || autosavePaused || !persistenceEnabled.current) return;
    setAcknowledged(unit); setExpected(unit.revision); setDraft(draftFromUnit(unit));
    setReceipt(null); setMessage("");
  }, [unit, unitId, acknowledged, dirty, exactDraftIsClean, busy, pending, refused, review, conflict, comparisonUnavailable, storageError, autosavePaused]);

  // Unsaved edits are kept in this tab's recovery, and a sent operation in its pending record, so
  // leaving never asks: both are there on return (O7, A2 S01).
  useEffect(() => {
    if (refused || !persistenceEnabled.current) return;
    try {
      if (dirty) writeRecovery(recoveryKey, { draft, revision: expected, unitId });
      else sessionStorage.removeItem(recoveryKey);
    } catch { setStorageError(true); }
  }, [draft, dirty, expected, recoveryKey, refused, unitId]);
  useEffect(() => {
    const sync = () => setPending(readPending(recoveryKey));
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [recoveryKey]);
  useEffect(() => {
    const clear = (event: Event) => {
      if (!recoveryKey.startsWith((event as CustomEvent<string>).detail)) return;
      persistenceEnabled.current = false;
      setDraft(draftFromUnit(null)); setAcknowledged(null); setConflict(null); setPending(null); setReview(false); setRefused(true);
      setReceipt(null); setMessage("Teaching access is no longer available. Private recovery content has been cleared.");
    };
    window.addEventListener(GUIDANCE_RECOVERY_CLEARED, clear);
    return () => window.removeEventListener(GUIDANCE_RECOVERY_CLEARED, clear);
  }, [recoveryKey]);
  // A receipt is said, then clears; a newer receipt is said for its own time.
  useEffect(() => {
    if (!receipt) return;
    const timer = window.setTimeout(() => setReceipt(current => (current === receipt ? null : current)), RECEIPT_SAID_FOR_MS);
    return () => window.clearTimeout(timer);
  }, [receipt]);

  const refreshConflict = async () => {
    const latest = await onConflict(unitId);
    if (!persistenceEnabled.current) return;
    setComparisonUnavailable(!latest);
    if (latest) setConflict(latest);
  };
  const accept = async (result: UnitOperation, operation: PendingGuidance) => {
    if (!persistenceEnabled.current) return false;
    writePending(recoveryKey, null, operation.id); setPending(readPending(recoveryKey));
    if (result.state !== "committed") {
      setReceipt(null); setMessage(result.message); await refreshConflict(); return false;
    }
    const saved = result.unit;
    setAutosavePaused(false); setComparisonUnavailable(false); setClassTaken(null);
    setAcknowledged(saved); setExpected(saved.revision); setConflict(null);
    // A receipt never writes text back into the draft: the teacher's own
    // characters (a later keystroke, a trailing space) stay exactly as typed,
    // and `dirty` compares saved content, so an acknowledged draft reads clean.
    onSaved(saved);
    setMessage(""); setReceipt({ kind: result.kind, unit: saved });
    // The receipt is historical. Refresh current truth separately, preserving
    // the acknowledgement if another teacher has since changed this unit.
    const latest = await onConflict(saved.id);
    if (!persistenceEnabled.current) return false;
    if (latest && latest.revision !== saved.revision) setConflict(latest);
    return true;
  };
  const failure = async (error: unknown, operation: PendingGuidance) => {
    if (!persistenceEnabled.current) return false;
    const existingId = classPlanExists(error);
    if (operation.kind === "publish" && error instanceof ApiError && error.status === 403
      && typeof error.detail === "object" && error.detail !== null && "code" in error.detail
      && error.detail.code === "guidance_primary_required") {
      writePending(recoveryKey, null, operation.id); setPending(readPending(recoveryKey));
      setReceipt(null); setMessage(error.message);
      setAcknowledged(saved => saved ? { ...saved, can_publish: false } : saved);
    } else if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
      clearGuidanceRecovery(recoveryKey); setDraft(draftFromUnit(null)); setRefused(true); setPending(null);
      setReceipt(null); setMessage("Teaching access is no longer available. Private recovery content has been cleared.");
    } else if (existingId) {
      writePending(recoveryKey, null, operation.id); setPending(readPending(recoveryKey)); setReceipt(null);
      if (operation.kind !== "create") {
        setAutosavePaused(true);
        const sent = operation.snapshot ? (JSON.parse(operation.snapshot) as UnitDraft).sessionId : draftRef.current.sessionId;
        setClassTaken({ sessionId: sent, unitId: existingId });
        setMessage("That session already has a plan. Choose another session, or edit its plan.");
        return false;
      }
      // Another teacher created this session's plan first. Load theirs and keep
      // these edits on it as a pending change; one session keeps one plan.
      const latest = await onConflict(existingId);
      if (!persistenceEnabled.current) return false;
      if (!latest) {
        setAutosavePaused(true);
        setMessage("This session already has a plan, and it could not be loaded. Your edits are kept here.");
        return false;
      }
      const baselineNew = draftFromUnit(null, defaults);
      setUnitId(latest.id); setAcknowledged(latest); setExpected(latest.revision); setAutosavePaused(false);
      setDraft(mine => mergeOnto(latest, baselineNew, mine));
      setAdoptedExisting(true);
    } else if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
      setAutosavePaused(true); setReceipt(null);
      writePending(recoveryKey, null, operation.id); setPending(readPending(recoveryKey)); setMessage(error.message);
      if (error.status === 409) await refreshConflict();
    } else {
      setReceipt(null);
      setMessage("The outcome is unknown. Check this exact operation before saving or publishing again. Closing does not cancel it.");
    }
    return false;
  };
  const perform = async (kind: PendingGuidance["kind"], report?: (outcome: UnitPublishOutcome) => void) => {
    const notSent = (reason: string) => { report?.({ kind: "refused", reason }); return false; };
    if (refused || !persistenceEnabled.current) return notSent("Teaching access is no longer available. Private recovery content has been cleared.");
    if (storageError) return notSent("Browser storage is unavailable. The plan cannot be saved safely.");
    if (pending || readPending(recoveryKey)) return notSent("The last plan operation is not confirmed. Check it before publishing.");
    if (busy) return notSent("Wait until the current plan operation finishes before publishing.");
    if (kind === "publish" && !acknowledged) return notSent("Publishing needs a saved plan.");
    if (kind === "publish" && acknowledged?.can_publish !== true)
      return notSent("The primary instructor publishes guidance. Your draft is saved.");
    const sentDraft = draftRef.current;
    if (kind !== "publish" && !validDraft(sentDraft)) return false;
    const operation = { id: crypto.randomUUID(), unitId, kind, snapshot: JSON.stringify(sentDraft) };
    try { writePending(recoveryKey, operation); }
    catch { setStorageError(true); return notSent("Browser storage is unavailable. Nothing was sent."); }
    operationFocus.current = true;
    setPending(operation); setBusy(true); setMessage("");
    try {
      if (kind === "publish") {
        const result = await courseUnitsApi.publish(courseId, unitId, {
          operation_id: operation.id, revision: acknowledged!.revision,
          saved_revision: acknowledged!.saved_revision, published_revision: acknowledged!.published_revision,
        });
        const confirmed = await accept(result, operation);
        report?.(confirmed ? { kind: "confirmed" } : { kind: "refused", reason: result.state !== "committed" ? result.message : "Teaching access is no longer available." });
        return confirmed;
      }
      const content = draftContent(sentDraft);
      return await accept(kind === "create"
        ? await courseUnitsApi.create(courseId, { ...content, id: unitId, operation_id: operation.id, position })
        : await courseUnitsApi.patch(courseId, unitId, { ...content, revision: expected!, operation_id: operation.id }), operation);
    } catch (error) {
      const result = await failure(error, operation);
      const refused = error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status);
      const reason = error instanceof ApiError ? error.message : "The publication outcome could not be confirmed.";
      report?.(refused ? { kind: "refused", reason } : { kind: "uncertain", reason, operationId: operation.id });
      return result;
    }
    finally { setBusy(false); }
  };
  /** Reads the pending operation's own receipt, and returns it, so a caller answers for that send alone. */
  const reconcile = async (): Promise<UnitOperation | undefined> => {
    if (!pending || busy) return undefined;
    const operation = pending;
    operationFocus.current = true; setBusy(true);
    try {
      const result = await courseUnitsApi.reconcile(courseId, operation.unitId, operation.id, operation.kind);
      await accept(result, operation);
      return result;
    }
    catch (error) { await failure(error, operation); return undefined; }
    finally { setBusy(false); }
  };
  const save = () => perform(acknowledged ? "save" : "create");
  const publishOutcome = async (): Promise<UnitPublishOutcome> => {
    let outcome: UnitPublishOutcome = { kind: "refused", reason: "The plan was not sent for publication." };
    await perform("publish", result => { outcome = result; });
    return outcome;
  };
  const disabled = busy || !!pending || refused || storageError;
  const valid = validDraft(draft);
  /** Autosave is armed: the next edit-free 800 ms sends this draft. */
  const autosaveArmed = dirty && !disabled && valid && !conflict && !comparisonUnavailable && !autosavePaused && !review;
  useEffect(() => {
    if (!autosaveArmed) return;
    const timer = window.setTimeout(() => { void save(); }, 800);
    return () => window.clearTimeout(timer);
    // Each edit gets its own debounce. Pending operations block new mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, autosaveArmed]);

  const resolveConflict = (keepMine: boolean) => {
    if (!conflict) return;
    setAcknowledged(conflict); setExpected(conflict.revision);
    if (!keepMine) setDraft(draftFromUnit(conflict));
    setConflict(null); setAutosavePaused(false);
  };

  return {
    unitId, draft, setDraft, acknowledged, dirty, valid, busy, pending, message, receipt, refused, storageError,
    conflict, comparisonUnavailable, adoptedExisting, classTaken, autosavePaused, autosaveArmed, review, setReview, disabled, operationFocus,
    save, publish: () => perform("publish"), publishOutcome, reconcile, refreshConflict,
    keepMine: () => resolveConflict(true), discardMine: () => resolveConflict(false),
    discardRecovery: () => sessionStorage.removeItem(recoveryKey),
    /** A new session was chosen after a refused placement: autosave may try again. */
    resumePlacement: () => { setClassTaken(null); setAutosavePaused(false); setMessage(""); },
  };
}

export type UnitDraftState = ReturnType<typeof useUnitDraft>;
